/** Small formatting helpers shared by every screen — keep display logic out of components. */
import { __, _n } from '../i18n.js';

/**
 * Integer cents -> Dutch-formatted euro string ("€ 19,99"). HH-2's first
 * money field in the app — kept here rather than a subscriptions-only
 * file since it's a generic formatter, not subscription vocabulary.
 */
export function formatCents(cents) {
  const value = (Number(cents) || 0) / 100;
  return `€ ${value.toLocaleString('nl-NL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function formatMinutes(min) {
  const m = Number(min) || 0;
  if (m < 60) return `${m} min`;
  const h = Math.floor(m / 60);
  const rem = m % 60;
  return rem ? `${h}u ${rem}m` : `${h}u`;
}

/** MySQL 'Y-m-d H:i:s' (UTC, per ETM_Tasks doc comment) -> local Date. */
export function parseServerDate(value) {
  if (!value) return null;
  const iso = value.includes('T') ? value : `${value.replace(' ', 'T')}Z`;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? null : d;
}

/**
 * Elapsed time since a task's `started_at` (server UTC datetime, see
 * parseServerDate()) as `m:ss`/`h:mm:ss`, for the Phase 5A timer UI
 * (TaskScreen). `nowMs` is a parameter rather than an internal Date.now()
 * call so the ticking display is driven entirely by the caller's own
 * interval/state, not a hidden clock read buried in a formatter.
 */
export function formatElapsed(startedAt, nowMs) {
  const start = parseServerDate(startedAt);
  if (!start) return '0:00';
  const totalSeconds = Math.max(0, Math.floor((nowMs - start.getTime()) / 1000));
  const h = Math.floor(totalSeconds / 3600);
  const m = Math.floor((totalSeconds % 3600) / 60);
  const s = totalSeconds % 60;
  const mm = String(m).padStart(2, '0');
  const ss = String(s).padStart(2, '0');
  return h > 0 ? `${h}:${mm}:${ss}` : `${m}:${ss}`;
}

/**
 * Relative time ("3 uur geleden") for the Phase 6 household activity feed
 * — `nowMs` is a parameter for the same reason formatElapsed()'s is: no
 * hidden Date.now() read buried in a formatter, the caller's own clock
 * drives it. Falls back to a plain short date past a week out, where
 * "X dagen geleden" stops being a useful unit.
 */
export function formatRelativeTime(value, nowMs) {
  const date = parseServerDate(value);
  if (!date) return '';
  const diffSeconds = Math.max(0, Math.round((nowMs - date.getTime()) / 1000));

  if (diffSeconds < 60) return __('zojuist', 'etm');

  const diffMinutes = Math.round(diffSeconds / 60);
  if (diffMinutes < 60) {
    return (diffMinutes === 1 ? __('%d minuut geleden', 'etm') : __('%d minuten geleden', 'etm')).replace(
      '%d',
      diffMinutes
    );
  }

  const diffHours = Math.round(diffMinutes / 60);
  if (diffHours < 24) {
    return __('%d uur geleden', 'etm').replace('%d', diffHours);
  }

  const diffDays = Math.round(diffHours / 24);
  if (diffDays < 7) {
    return (diffDays === 1 ? __('%d dag geleden', 'etm') : __('%d dagen geleden', 'etm')).replace('%d', diffDays);
  }

  return date.toLocaleDateString('nl-NL', { day: 'numeric', month: 'short' });
}

/**
 * Whole-day difference between today and a 'Y-m-d' deadline. Mirrors
 * ETM_Engine::days_until() — both "today" and the deadline are anchored to
 * UTC midnight, matching the backend (see class-etm-engine.php's docblock:
 * everything there is deliberately UTC so day-difference math never drifts
 * against a site's timezone). Anchoring "today" to the *browser's* local
 * midnight instead would disagree with the backend by up to a day for
 * anyone west of UTC in the evening or east of UTC after midnight local
 * time but before UTC midnight.
 */
export function daysUntil(deadline) {
  if (!deadline) return null;
  const now = new Date();
  const todayUtc = Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate());
  const target = new Date(`${deadline}T00:00:00Z`);
  if (Number.isNaN(target.getTime())) return null;
  return Math.round((target.getTime() - todayUtc) / 86400000);
}

/**
 * Short relative deadline label ("Over 3 dagen"/"Vandaag"/"Morgen"/
 * "Verlopen") — moved here from ManageScreen (WP-2) so the new
 * UpcomingPanel can share it instead of duplicating the same four lines.
 */
export function deadlineChipLabel(deadline) {
  const days = daysUntil(deadline);
  if (days === null) return null;
  if (days < 0) return __('Verlopen', 'etm');
  if (days === 0) return __('Vandaag', 'etm');
  if (days === 1) return __('Morgen', 'etm');
  return `${__('Over', 'etm')} ${days} ${_n('dag', 'dagen', days, 'etm')}`;
}
